// Small, dependency-free markdown renderer for memory content (dimension prose,
// node descriptions, artifact documents). Client-safe. Covers what the sync
// agent actually writes: headers, bold, inline code, code fences, bullets,
// checklists, blockquotes — rendered in the ambient voice (no heavy chrome).

import React from "react";
import CopyBlock from "./CopyBlock";

function inline(text: string, key: number): React.ReactNode {
  const parts: React.ReactNode[] = [];
  // ![alt](src) images, **bold**, `code`, and [label](href) links.
  // The image alternative MUST come first — otherwise `[alt](src)` matches the
  // inside of `![alt](src)` and the bang is left stranded as literal text, which
  // is exactly how the weekly letter's feature cards first rendered.
  // `*italic*` rides after bold so `**strong**` is never read as two stars
  // around an italic; the star must open against a non-space and close against
  // one, so a bullet's leading "* " or a bare "2 * 3" never turns italic.
  const re = /(!\[[^\]]*\]\([^)\s]+\)|\*\*[^*]+\*\*|\*(?=\S)[^*\n]+?(?<=\S)\*|`[^`]+`|\[[^\]]+\]\([^)\s]+\))/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("![")) {
      // ![alt](src) — a real image. Block-level by nature: it breaks out of the
      // paragraph flow so a generated card can sit beside the prose that
      // explains it, at the reading measure and never wider.
      const cut = tok.indexOf("](");
      const alt = tok.slice(2, cut);
      const src = tok.slice(cut + 2, -1);
      parts.push(
        // eslint-disable-next-line @next/next/no-img-element
        <img
          key={`${key}-${i++}`}
          src={src}
          alt={alt}
          loading="lazy"
          className="im-md__img"
        />,
      );
    } else if (tok.startsWith("[")) {
      // [label](href) — internal links stay in-tab; external ones open safely.
      const cut = tok.indexOf("](");
      const label = tok.slice(1, cut);
      const href = tok.slice(cut + 2, -1);
      const external = /^https?:\/\//.test(href);
      parts.push(
        <a
          key={`${key}-${i++}`}
          href={href}
          {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
          style={{ color: "var(--accent-ink)", textDecoration: "underline", textUnderlineOffset: "0.15em" }}
        >
          {label}
        </a>,
      );
    } else if (tok.startsWith("**"))
      parts.push(
        <strong key={`${key}-${i++}`} style={{ fontWeight: 700, color: "var(--ink)" }}>
          {tok.slice(2, -2)}
        </strong>,
      );
    else if (tok.startsWith("*"))
      parts.push(
        <em key={`${key}-${i++}`} style={{ fontStyle: "italic", color: "var(--ink)" }}>
          {tok.slice(1, -1)}
        </em>,
      );
    else
      parts.push(
        <code
          key={`${key}-${i++}`}
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "0.86em",
            background: "color-mix(in oklab, var(--organism-color) 12%, var(--bg))",
            padding: "0.08em 0.4em",
            borderRadius: 4,
            color: "var(--accent-ink)",
          }}
        >
          {tok.slice(1, -1)}
        </code>,
      );
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

// artifact header sizes — kept in lockstep with `.art-prose .tiptap h1..h4` in
// globals.css so a card swapping the Markdown fallback for the TipTap renderer (or a
// swipe between docs) never resizes a heading.
const ART_H: Record<number, string> = { 1: "1.5rem", 2: "1.28rem", 3: "1.12rem", 4: "1rem" };

export default function Markdown({ source, style, variant = "default" }: { source: string; style?: React.CSSProperties; variant?: "default" | "artifact" }) {
  const out: React.ReactNode[] = [];
  const lines = source.split("\n");
  let i = 0;
  let k = 0;

  while (i < lines.length) {
    const line = lines[i];

    // code fence
    if (line.trimStart().startsWith("```")) {
      const buf: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trimStart().startsWith("```")) buf.push(lines[i++]);
      i++; // closing fence
      const codeText = buf.join("\n");
      out.push(
        <CopyBlock key={k++} text={codeText} raw>
        <pre
          style={{
            fontFamily: "var(--font-mono)",
            // the reader pages breathe a little more than the ambient voice
            fontSize: variant === "artifact" ? "0.85rem" : "0.78rem",
            lineHeight: 1.68,
            color: "var(--ink-2)",
            background: "color-mix(in oklab, var(--organism-color) 6%, var(--bg))",
            border: "1px solid var(--hair)",
            borderRadius: "var(--radius-card)",
            padding: variant === "artifact" ? "1rem 1.2rem" : "0.85rem 1.05rem",
            overflowX: "auto",
            margin: variant === "artifact" ? "1.1rem 0" : "0.7rem 0",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {buf.join("\n")}
        </pre>
        </CopyBlock>,
      );
      continue;
    }

    // headers
    const h = line.match(/^(#{1,4})\s+(.*)/);
    if (h) {
      const level = h[1].length;
      out.push(
        <div
          key={k++}
          style={
            variant === "artifact"
              ? {
                  fontSize: ART_H[level] ?? "1rem",
                  color: level >= 4 ? "var(--ink-2)" : "var(--ink)",
                  fontWeight: 400,
                  letterSpacing: level === 1 ? "-0.03em" : level >= 4 ? "0.04em" : "-0.015em",
                  textTransform: level >= 4 ? ("lowercase" as const) : undefined,
                  lineHeight: 1.15,
                  margin: level === 1 ? "1.5rem 0 0.6rem" : "1.3rem 0 0.45rem",
                }
              : {
                  fontSize: level <= 2 ? "1.02rem" : "0.9rem",
                  color: "var(--ink)",
                  letterSpacing: level > 2 ? "0.06em" : "-0.005em",
                  textTransform: level > 2 ? ("lowercase" as const) : undefined,
                  margin: "1.1rem 0 0.4rem",
                }
          }
        >
          {inline(h[2], k)}
        </div>,
      );
      i++;
      continue;
    }

    // pipe table — a header row, a `|---|` rule, then rows. The comparisons'
    // "side by side" is one of these; it renders as the house ledger (the
    // .md-table grammar in globals.css): hairlines, no boxes, the first column
    // faint like a detail-row label. A cell may carry inline markdown.
    const isRow = (l: string) => /^\s*\|.*\|\s*$/.test(l);
    const isRule = (l: string) => /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?\s*$/.test(l);
    if (isRow(line) && i + 1 < lines.length && isRule(lines[i + 1])) {
      const cells = (l: string) =>
        l
          .trim()
          .replace(/^\||\|$/g, "")
          .split("|")
          .map((c) => c.trim());
      const head = cells(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && isRow(lines[i])) rows.push(cells(lines[i++]));
      out.push(
        <div key={k++} className="md-table__wrap">
          <table className="md-table">
            <thead>
              <tr>
                {head.map((c, j) => (
                  <th key={j} scope="col">{inline(c, k * 100 + j)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri}>
                  {r.map((c, j) =>
                    j === 0 ? (
                      <th key={j} scope="row">{inline(c, k * 1000 + ri * 10 + j)}</th>
                    ) : (
                      <td key={j}>{inline(c, k * 1000 + ri * 10 + j)}</td>
                    ),
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    // blockquote — two voices. The default (chat, ambient) keeps the quiet
    // rule. The ARTIFACT voice — every reader page — drops the rule entirely
    // and lets the words carry it: larger, ink-forward, air above and below.
    // A quote in a letter is a held breath, not a sidebar.
    if (line.startsWith(">")) {
      const buf: string[] = [];
      while (i < lines.length && lines[i].startsWith(">")) buf.push(lines[i++].replace(/^>\s?/, ""));
      out.push(
        <blockquote
          key={k++}
          style={
            variant === "artifact"
              ? {
                  border: 0,
                  padding: 0,
                  margin: "1.7rem 0",
                  fontSize: "1.24em",
                  lineHeight: 1.5,
                  letterSpacing: "-0.012em",
                  color: "var(--ink)",
                  fontStyle: "italic",
                  maxWidth: "30ch",
                  textWrap: "balance" as const,
                }
              : {
                  borderLeft: "2px solid var(--accent)",
                  paddingLeft: "0.9rem",
                  margin: "0.7rem 0",
                  color: "var(--ink-2)",
                  fontStyle: "italic",
                }
          }
        >
          {buf.map((b, j) => (
            <div key={j}>{inline(b, k * 100 + j)}</div>
          ))}
        </blockquote>,
      );
      continue;
    }

    // checklist / bullets
    const li = line.match(/^\s*[-*]\s+(\[[ xX~]\]\s+)?(.*)/);
    if (li) {
      const items: { done: boolean | null; text: string }[] = [];
      while (i < lines.length) {
        const it = lines[i].match(/^\s*[-*]\s+(\[[ xX~]\]\s+)?(.*)/);
        if (!it) break;
        items.push({ done: it[1] ? /x/i.test(it[1]) : null, text: it[2] });
        i++;
      }
      out.push(
        <ul key={k++} style={{ listStyle: "none", margin: "0.45rem 0", display: "flex", flexDirection: "column", gap: "0.32rem" }}>
          {items.map((it, j) => (
            <li key={j} style={{ display: "flex", gap: "0.55rem", alignItems: "baseline", color: "var(--ink-2)" }}>
              <span aria-hidden style={{ flexShrink: 0, fontSize: "0.7em", color: it.done ? "var(--accent-ink)" : "var(--ink-4)" }}>
                {it.done === null ? "·" : it.done ? "✓" : "○"}
              </span>
              <span style={{ textDecoration: it.done ? "line-through" : undefined, opacity: it.done ? 0.6 : 1 }}>
                {inline(it.text, k * 100 + j)}
              </span>
            </li>
          ))}
        </ul>,
      );
      continue;
    }

    // horizontal rule — a section break (---, ***, ___)
    if (/^\s*([-*_])\1{2,}\s*$/.test(line)) {
      out.push(
        <hr
          key={k++}
          style={{
            border: 0,
            height: 0,
            borderTop: "1px solid var(--hair)",
            width: "38%",
            margin: "1.9rem auto",
            opacity: 0.7,
          }}
        />,
      );
      i++;
      continue;
    }

    // blank
    if (!line.trim()) {
      i++;
      continue;
    }

    // paragraph
    out.push(
      <p key={k++} style={{ margin: "0.45rem 0", lineHeight: 1.66, color: "var(--ink-2)" }}>
        {inline(line, k)}
      </p>,
    );
    i++;
  }

  return <div className="im-md" style={{ fontSize: "0.92rem", ...style }}>{out}</div>;
}
